import { useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import WorldMap from "@/components/WorldMap";
import RegionalFeed from "@/components/RegionalFeed";
import { api } from "@/lib/api";
import { IMAGES, STATE_LABEL } from "@/lib/content";

const itemCountry = (x) => x.country || (x.region ? "India" : "");
const itemRegions = (x) => (x.regions?.length ? x.regions : [x.region]).filter(Boolean);

export default function WhereWeWork() {
  const [sel, setSel] = useState(null);
  const { data: programmes = [] } = useQuery({ queryKey: ["programmes"], queryFn: async () => (await api.get("/programmes")).data });
  const { data: stories = [] } = useQuery({ queryKey: ["stories"], queryFn: async () => (await api.get("/stories")).data });
  const { data: journeys = [] } = useQuery({ queryKey: ["journeys"], queryFn: async () => (await api.get("/journeys")).data });

  const { countryCounts, stateCounts, experienceCounts } = useMemo(() => {
    const cc = {};
    const sc = {};
    const ec = {};
    [...programmes, ...stories].forEach((x) => {
      const c = itemCountry(x);
      if (!c) return;
      cc[c] = (cc[c] || 0) + 1;
      if (c === "India") itemRegions(x).forEach((r) => { sc[r] = (sc[r] || 0) + 1; });
    });
    journeys.forEach((j) => {
      const c = itemCountry(j);
      if (c) ec[c] = (ec[c] || 0) + 1;
      itemRegions(j).forEach((r) => { sc[r] = (sc[r] || 0) + 1; });
    });
    return { countryCounts: cc, stateCounts: sc, experienceCounts: ec };
  }, [programmes, stories, journeys]);

  const matchesSel = useCallback(
    (x) => (sel ? (sel.type === "country" ? itemCountry(x) === sel.name : itemRegions(x).includes(sel.name)) : false),
    [sel]
  );
  const feedProgrammes = useMemo(() => programmes.filter(matchesSel), [programmes, matchesSel]);
  const feedStories = useMemo(() => stories.filter(matchesSel), [stories, matchesSel]);
  const feedExperiences = useMemo(() => journeys.filter(matchesSel), [journeys, matchesSel]);
  const selLabel = sel ? (sel.type === "state" ? STATE_LABEL[sel.name] || sel.name : sel.name) : "";

  return (
    <>
      <Seo title="Where We Work" path="/where-we-work" description="Where Planet Trotter Foundation works: rooted in India, with programmes intended to expand worldwide. The map shows only real, published work." />
      <PageHero
        eyebrow="Where We Work"
        titleLines={["Where the", "Foundation works."]}
        intro="Rooted in India, with programmes intended to expand worldwide. This map lights up only where real, published programmes and stories exist. Nothing is claimed in advance."
        image={IMAGES.river}
      />

      <section className="bg-ptf-bg py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <p className="text-[0.7rem] uppercase tracking-[0.28em] text-ptf-primary/60 mb-5 flex items-center gap-3">
              <span className="h-px w-8 bg-ptf-primary/40 inline-block" /> Two kinds of presence
            </p>
            <h2 className="font-display font-extrabold uppercase text-3xl md:text-4xl tracking-tight text-ptf-primary leading-[0.95]">
              Programmes and field experiences are mapped separately.
            </h2>
            <p className="mt-6 text-ptf-text/75 leading-relaxed">
              A programme means the Foundation is undertaking work in a place. A field
              experience means people can travel, learn and document there with us.
              One never implies the other.
            </p>
            <div data-testid="map-legend" className="mt-8 space-y-3">
              <p className="flex items-center gap-3 text-sm text-ptf-text/80">
                <span className="inline-block h-3.5 w-3.5 rounded-full bg-ptf-accent border border-ptf-primary/40" />
                Foundation programmes & stories
              </p>
              <p className="flex items-center gap-3 text-sm text-ptf-text/80">
                <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-ptf-secondary bg-ptf-secondary/25" />
                Field experiences
              </p>
            </div>
          </div>
          <div className="lg:col-span-8">
            <WorldMap countryCounts={countryCounts} stateCounts={stateCounts} experienceCounts={experienceCounts} selected={sel} onSelect={setSel} />
          </div>
        </div>
        {sel && (
          <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 mt-12">
            <RegionalFeed title={selLabel} programmes={feedProgrammes} stories={feedStories} experiences={feedExperiences} onClear={() => setSel(null)} />
          </div>
        )}
      </section>
    </>
  );
}
